import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Injector,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  forwardRef
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { isEqual } from "lodash";
import { filter, takeUntil } from "rxjs/operators";

import { BehaviorSubject, Subscription } from "rxjs";

import { defaultThreshold } from "../../fuse/fuse-constants";
import {
  AsyncOptionsIterator,
  AsyncOptionsIteratorResult,
  BaseOptionsDirective,
  BaseSelectComponent,
  SELECT_CLASSES,
  SelectClasses,
  getSelectClasses
} from "../base";

import { AvailableOptionsListComponent } from "./available-options-list.component";
import { SelectedOptionsListComponent } from "./selected-options-list.component";

@Component({
  selector: "ta-transfer-list",
  templateUrl: "./transfer-list.template.html",
  providers: [
    {
      provide: SELECT_CLASSES,
      useValue: getSelectClasses("transfer-list")
    },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TransferListComponent),
      multi: true
    },
    {
      provide: BaseSelectComponent,
      useExisting: forwardRef(() => TransferListComponent)
    }
  ]
})
export class TransferListComponent<
  Model,
  Identifier extends number | string,
  FieldValue extends Model | Identifier
> extends BaseSelectComponent<Model, Identifier, FieldValue>
  implements OnInit, AfterViewInit, ControlValueAccessor {
  @ViewChild(
    forwardRef(() => AvailableOptionsListComponent),
    { static: false }
  )
  availableList: AvailableOptionsListComponent<Model>;

  @ViewChild(
    forwardRef(() => SelectedOptionsListComponent),
    { static: false }
  )
  selectedList: SelectedOptionsListComponent<Model>;

  @ViewChild("controls", { static: false }) controlsElement: ElementRef;

  @HostBinding("attr.tabindex")
  tabIndex = 0;

  @Input()
  get value(): FieldValue[] {
    return this._value;
  }
  set value(input: FieldValue[]) {
    if (!this.isNewSelection(input, this.value)) {
      this.initializedModel = true;
      return;
    }
    this._value = input;
    this.updateSelectedModel(input);
  }
  private _value: FieldValue[] = [];

  @Input()
  get selectedModels(): Model[] {
    return this._selectedModels;
  }
  set selectedModels(models: Model[]) {
    let identifiers: Identifier[] = models.map((model) =>
      this.getIdentifierFromModel(model)
    );

    let values: FieldValue[];
    if (models == null) {
      values = [];
    } else if (this.valueBy === "model") {
      values = models as FieldValue[];
    } else {
      values = identifiers as FieldValue[];
    }
    if (this.isNewSelection(identifiers, this.selectedIdentifiers)) {
      this.selectedIdentifiers = identifiers;
      this._selectedModels = models;
      this._value = values;

      this.selectedModelsChange.emit(models);
      this.selectedOptions.next(models != null ? models : []);
      this.valueChange.emit(values);
    }

    if (!this.initializedModel) {
      this.initializedModel = true;
    }
  }
  private _selectedModels: Model[] = [];

  @Input()
  ignoreSelectAllCache = false;

  @Input()
  mirror: boolean;

  @Input()
  disabled = false;

  @Input()
  disallowedOptions: Identifier[] = [];

  @Input()
  disableDisallowedOptions = false;

  @Input()
  searchThreshold: number = defaultThreshold;

  @Output()
  valueChange: EventEmitter<FieldValue[]> = new EventEmitter<FieldValue[]>();

  @Output()
  selectedModelsChange: EventEmitter<Model[]> = new EventEmitter<Model[]>();

  private selectedIdentifiers: Identifier[] = [];

  selectedOptions: BehaviorSubject<Model[]> = new BehaviorSubject<Model[]>([]);
  availableOptions: BehaviorSubject<Model[]> = new BehaviorSubject<Model[]>([]);

  constructor(
    injector: Injector,
    optionsSourceDirective: BaseOptionsDirective<Model, Identifier>,
    @Inject(SELECT_CLASSES) selectClasses: SelectClasses,
    elementRef: ElementRef,
    renderer: Renderer2
  ) {
    super(
      injector,
      optionsSourceDirective,
      selectClasses,
      elementRef,
      renderer
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initLoadedOptionsSubscription();
    this.getOptions({ filters: this.filters, search: this.queryString.value });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.renderer.addClass(
      this.controlsElement.nativeElement,
      this.selectClasses.controls
    );
  }

  private initLoadedOptionsSubscription(): void {
    this.loadedOptions
      .pipe(
        filter((models: Model[]) => {
          return [
            models.length,
            this.selectedModels.length,
            this.availableOptions.value.length
          ].some((val) => val > 0);
        })
      )
      .subscribe((models: Model[]) => this.sortLoadedModels(models));
  }

  protected initParamsSubscription(): void {
    super.initParamsSubscription();
    this.filtersChange
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe((filters?: object) => {
        // transfer list caches calls to its selectAll method
        // that cache should expire if filters change
        this.selectAllCache = {};
      });
  }

  isSelected(model: Model): boolean {
    return (
      this.selectedIdentifiers != null &&
      this.selectedIdentifiers.includes(this.getIdentifierFromModel(model))
    );
  }

  setSelected(models: Model[]): void {
    // nb, this *clears* selected and replaces it with param
    this.availableOptions.next([
      ...this.availableOptions.value,
      ...this.selectedOptions.value
    ]);
    this._selectedModels = [];
    this.addToSelected(models);
  }

  transferToSelected(): void {
    if (this.availableOptions.value.length === 0) {
      return;
    }
    if (this.availableList.selected.value.length === 0) {
      this.addToSelected([this.availableList.options[0]]);
    } else {
      this.addToSelected(this.availableList.selected.value);
    }

    this.availableList.clearSelected();
    this.selectedList.clearSelected();
  }

  transferToAvailable(): void {
    if (this.selectedOptions.value.length === 0) {
      return;
    }
    if (
      this.selectedList.selected.value.length === 0 &&
      this.disableDisallowedOptions
    ) {
      const allowedOptions = this.selectedList.options.filter(
        (model) =>
          !this.disallowedOptions.includes(
            this.getIdentifierFromModel(model)
          ) || !this.disableDisallowedOptions
      );
      if (allowedOptions.length > 0) {
        this.addToAvailable([allowedOptions[0]]);
      }
    } else if (this.selectedList.selected.value.length === 0) {
      this.addToAvailable([this.selectedList.options[0]]);
    } else {
      this.addToAvailable(this.selectedList.selected.value);
    }

    this.availableList.clearSelected();
    this.selectedList.clearSelected();
  }

  private selectAllCache: { [key: string]: Model[] } = {};
  async selectAll(): Promise<Model[]> {
    if (!this.ignoreSelectAllCache) {
      const cachedItems: Model[] = this.selectAllCache[
        this.availableList.queryString
      ];
      if (cachedItems != null) {
        this.addToSelected(cachedItems);
        return cachedItems;
      }
    }
    const res = this.optionsSource({
      search: this.availableList.queryString || undefined,
      filters: this.filters
    });
    let items: Model[];
    if (Array.isArray(res)) {
      items = res;
    } else if (res instanceof Promise) {
      items = await res;
    } else {
      items = await this.getAllOptionsFromAsyncIterator(res);
    }

    this.addToSelected(items);
    this.selectAllCache[this.availableList.queryString] = items;
    return items;
  }

  private async getAllOptionsFromAsyncIterator(
    iterator: AsyncOptionsIterator<Model>
  ): Promise<Model[]> {
    let res: AsyncOptionsIteratorResult<Model>,
      items: Model[] = [];

    do {
      res = await iterator.next();
      items.push(...res.value);
    } while (!res.done);

    return items;
  }

  unselectAll(): void {
    const allowedOptions = this.selectedList.options.filter(
      (model) =>
        !this.disallowedOptions.includes(this.getIdentifierFromModel(model)) ||
        !this.disableDisallowedOptions
    );
    this.addToAvailable(allowedOptions);
  }

  addToSelected(models: Model[]): void {
    const selectedMap: Map<Identifier, Model> = this.getSelectedMap();
    const availableMap: Map<Identifier, Model> = this.getAvailableMap();

    let identifier: Identifier;
    models.forEach((model) => {
      identifier = this.getIdentifierFromModel(model);
      availableMap.delete(identifier);
      selectedMap.set(identifier, model);
    });
    this.selectedModels = Array.from(selectedMap.values());
    this.availableOptions.next(Array.from(availableMap.values()));
  }

  addToAvailable(models: Model[], reset: boolean = false): void {
    const selectedMap: Map<Identifier, Model> = this.getSelectedMap();
    const availableMap: Map<Identifier, Model> = reset
      ? new Map<Identifier, Model>()
      : this.getAvailableMap();

    let identifier: Identifier;
    models.forEach((model) => {
      identifier = this.getIdentifierFromModel(model);
      selectedMap.delete(identifier);
      availableMap.set(identifier, model);
    });
    this.selectedModels = Array.from(selectedMap.values());
    this.availableOptions.next(Array.from(availableMap.values()));
  }

  private sortLoadedModels(models: Model[]): void {
    this.addToAvailable(
      models.filter((model) => !this.isSelected(model)),
      true
    );
    this.addToSelected(models.filter((model) => this.isSelected(model)));
  }

  private getSelectedMap(): Map<Identifier, Model> {
    return this.selectedModels.reduce((map, model): Map<Identifier, Model> => {
      return map.set(this.getIdentifierFromModel(model), model);
    }, new Map<Identifier, Model>());
  }

  private getAvailableMap(): Map<Identifier, Model> {
    return this.availableOptions.value.reduce((map, model): Map<
      Identifier,
      Model
    > => {
      return map.set(this.getIdentifierFromModel(model), model);
    }, new Map<Identifier, Model>());
  }

  private updateSelectedModelSubscription?: Subscription;

  private updateSelectedModel(value?: FieldValue[]): void {
    if (
      this.updateSelectedModelSubscription != null &&
      !this.updateSelectedModelSubscription.closed
    ) {
      this.updateSelectedModelSubscription.unsubscribe();
    }

    if (value == null || !value.length) {
      this.updateSelectedModelSubscription = undefined;
      this.setSelected([]);
      return;
    }

    this.updateSelectedModelSubscription = this.getModelsFromFieldValues(
      value
    ).subscribe(
      (models) => {
        this.setSelected(models);
      },
      () => {
        // TODO: error, failure to lookup value
        throw new Error();
      }
    );
  }

  private isNewSelection(arr1: Identifier[], arr2: Identifier[]): boolean;
  private isNewSelection(arr1: FieldValue[], arr2: FieldValue[]): boolean;
  private isNewSelection<T>(arr1: T[], arr2: T[]): boolean {
    if (arr1 == null && arr2 == null) {
      return false;
    }
    if (arr1 == null || arr2 == null) {
      return true;
    }
    if (arr1.length !== arr2.length) {
      return true;
    }
    return !isEqual([...arr1].sort(), [...arr2.sort()]);
  }

  initControlAccessors(): void {
    super.initControlAccessors();

    this.valueChange
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe((value) => {
        if (this._onChangeFn == null || !this.initializedModel) {
          return;
        }
        this._onChangeFn(value);
      });
  }
}
